import { Alert, Group, Loader, Stack, Table, Text, Title } from '@mantine/core'
import { IconDatabase } from '@tabler/icons-react'
import {
  OWLBERT_CLOUD_KB_EMBEDDING_DIMENSIONS,
  isOwlbertManagedForKnowledgePolicy,
} from '@owlbert/core'
import React from 'react'
import { Link } from 'react-router-dom'
import { useOwlbertWebAuth } from '../../auth/OwlbertWebAuthContext'

type EmbeddingCatalogModel = {
  id: string
  provider: string
  model_id: string
  name: string
}

export const WebHostedKnowledgeSettingsPanel: React.FC = () => {
  const auth = useOwlbertWebAuth()
  const [models, setModels] = React.useState<EmbeddingCatalogModel[]>([])
  const [defaultEmbeddings, setDefaultEmbeddings] = React.useState<{ provider: string; model_id: string; name: string } | null>(
    null,
  )
  const [serverDims, setServerDims] = React.useState<number | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const managed = isOwlbertManagedForKnowledgePolicy({
    hostedSessionActive: auth.sessionReady,
    isProMode: auth.isProMode,
    proDesktopBuild: false,
    owlbertManagedKnowledge: undefined,
  })

  React.useEffect(() => {
    if (!auth.sessionReady || !auth.bundle?.client) {
      setModels([])
      setDefaultEmbeddings(null)
      setServerDims(null)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    const client = auth.bundle.client
    void Promise.all([
      client.embeddings.getModels.query(),
      client.knowledgebase.getEmbeddingDimensions.query(),
    ])
      .then(([emb, dims]) => {
        if (cancelled) return
        const rows = (emb.models ?? []) as EmbeddingCatalogModel[]
        setModels(rows.filter((m) => m.provider !== 'custom'))
        setDefaultEmbeddings(emb.defaultEmbeddings ?? null)
        setServerDims(typeof dims === 'number' ? dims : null)
      })
      .catch((e) => {
        if (cancelled) return
        setError(String((e as Error)?.message ?? e))
        setModels([])
        setDefaultEmbeddings(null)
        setServerDims(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [auth.bundle?.client, auth.sessionReady])

  if (!auth.sessionReady) {
    return (
      <Stack gap="sm" maw={720}>
        <Text size="sm" c="dimmed">
          Sign in to see embedding models and knowledgebase settings for your workspace.
        </Text>
      </Stack>
    )
  }

  const dimsMatch = serverDims == null || serverDims === OWLBERT_CLOUD_KB_EMBEDDING_DIMENSIONS

  return (
    <Stack gap="md" maw={900}>
      <Group gap="sm" wrap="nowrap">
        <IconDatabase size={28} />
        <div>
          <Title order={4}>Knowledge and embeddings</Title>
          <Text size="sm" c="dimmed">
            Manage files and indexing from the{' '}
            <Link to="/user-documents" style={{ fontWeight: 600 }}>
              Documents
            </Link>{' '}
            page.
          </Text>
        </div>
      </Group>

      {managed ? (
        <Alert color="blue" title="Hosted knowledge">
          In the web app, embedding and search indexing are configured for you. You can still add documents and refresh
          indexes from Documents.
        </Alert>
      ) : null}

      <Alert color={dimsMatch ? 'gray' : 'yellow'} title="Embedding size check">
        This app indexes text using{' '}
        <Text span fw={700} component="span">
          {OWLBERT_CLOUD_KB_EMBEDDING_DIMENSIONS}
        </Text>{' '}
        dimensions per snippet.{' '}
        {serverDims != null ? (
          <>
            The server reports <Text span fw={600}>{String(serverDims)}</Text> for stored snippets.
            {!dimsMatch ? ' If these differ, ask your admin to align defaults, then re-index.' : null}
          </>
        ) : (
          'Could not read the server’s stored embedding size.'
        )}
      </Alert>

      {defaultEmbeddings ? (
        <Text size="sm">
          Default embedding model for automatic embeddings:{' '}
          <Text span fw={600}>
            {defaultEmbeddings.name}
          </Text>{' '}
          <Text span c="dimmed" ff="monospace" size="xs">
            ({defaultEmbeddings.provider}:{defaultEmbeddings.model_id})
          </Text>
        </Text>
      ) : null}

      {loading ? (
        <Group gap="xs">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            Loading embedding catalog…
          </Text>
        </Group>
      ) : error ? (
        <Alert color="red" title="Could not load embeddings">
          {error}
        </Alert>
      ) : (
        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Id</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {models.map((m) => (
              <Table.Tr key={m.id}>
                <Table.Td>{m.name}</Table.Td>
                <Table.Td>
                  <Text size="sm" ff="monospace">
                    {m.id}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  )
}
